import { Plus, X } from 'lucide-react'
import { memo, useCallback, useMemo, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spin,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  ScrollArea,
  MarqueeText,
} from '@fp/ui-kit'
import { cn } from '@lib/cn'

const MAX_TAG_LENGTH = 70
const tagChipClass = 'gap-0.5 max-w-45 overflow-hidden'

const normalize = (v: string) => v.trim().toLowerCase()
const substringFilter = (value: string, search: string) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)

interface TagPickerProps {
  selected: string[]
  allTags: string[]
  onChange: (next: string[]) => void
  isLoading?: boolean
  disabled?: boolean
  emptyText?: string
}

export const TagPicker = memo(
  ({ selected, allTags, onChange, isLoading = false, disabled = false, emptyText = 'Add tag' }: TagPickerProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')

    const selectedSet = useMemo(() => new Set(selected.map(normalize)), [selected])

    const available = useMemo(() => {
      const seen = new Set<string>()
      const out: string[] = []
      for (const t of allTags) {
        const v = t?.toString() ?? ''
        if (!v) continue
        const k = normalize(v)
        if (seen.has(k) || selectedSet.has(k)) continue
        seen.add(k)
        out.push(v)
      }

      return out
    }, [allTags, selectedSet])

    const trimmed = query.trim()
    const exactExists = useMemo(
      () => trimmed.length > 0 && [...allTags, ...selected].some((t) => normalize(t) === normalize(trimmed)),
      [trimmed, allTags, selected],
    )
    const showCreate = trimmed.length > 0 && !exactExists && trimmed.length <= MAX_TAG_LENGTH

    const handleAdd = useCallback(
      (tag: string) => {
        const v = tag.trim()
        if (!v) return
        if (selected.some((t) => normalize(t) === normalize(v))) return
        onChange([...selected, v])
        setQuery('')
      },
      [selected, onChange],
    )

    const handleRemove = useCallback(
      (tag: string) => {
        const next = selected.filter((t) => t !== tag)
        if (next.length === selected.length) return
        onChange(next)
      },
      [selected, onChange],
    )

    const handleCreate = useCallback(() => {
      if (!showCreate) return
      handleAdd(trimmed)
    }, [showCreate, handleAdd, trimmed])

    return (
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (disabled) return
          setOpen(next)
          if (!next) setQuery('')
        }}
      >
        <PopoverTrigger asChild>
          <div
            role='button'
            tabIndex={0}
            className='group/cell flex h-full min-h-9.5 min-w-0 cursor-pointer flex-wrap items-center gap-2'
          >
            {selected.length > 0 ? (
              selected.map((tag) => (
                <Tooltip key={tag}>
                  <TooltipTrigger asChild>
                    <Badge shape='tag' size='tag' variant='secondary' className={cn(tagChipClass, 'pr-1')}>
                      <span className='opacity-50'>#</span>
                      <MarqueeText speed={25}>{tag}</MarqueeText>
                      <button
                        type='button'
                        aria-label={`Remove ${tag}`}
                        className='shrink-0 rounded-sm opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none group-hover/cell:opacity-100'
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleRemove(tag)
                        }}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={0}>{tag}</TooltipContent>
                </Tooltip>
              ))
            ) : (
              <Badge
                shape='tag'
                size='tag'
                variant='outline'
                className='gap-1 border-dashed opacity-50 transition-opacity group-hover/cell:opacity-100'
              >
                <Plus size={12} />
                <span>{emptyText}</span>
              </Badge>
            )}
            {isLoading && <Spin />}
          </div>
        </PopoverTrigger>
        <PopoverContent align='start' sideOffset={6} collisionPadding={12} className='w-72 p-0'>
          <div className='relative'>
            <Command filter={substringFilter}>
              <CommandInput
                value={query}
                onValueChange={(v) => setQuery(v.slice(0, MAX_TAG_LENGTH))}
                placeholder='Search or create tag...'
                maxLength={MAX_TAG_LENGTH}
                className='pr-10'
              />
              <CommandList className='max-h-60'>
                <TooltipProvider>
                  {showCreate && (
                    <CommandItem
                      key='__create__'
                      value={`__create__${trimmed}`}
                      onSelect={handleCreate}
                      forceMount
                      className='flex items-center gap-2'
                    >
                      <Plus size={14} className='shrink-0 text-muted-foreground' />
                      <span className='shrink-0'>Create</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='min-w-0 flex-1 truncate font-medium'>&ldquo;{trimmed}&rdquo;</span>
                        </TooltipTrigger>
                        <TooltipContent>{trimmed}</TooltipContent>
                      </Tooltip>
                    </CommandItem>
                  )}
                  {available.length > 0 && (
                    <CommandGroup heading={selected.length > 0 ? 'Available' : undefined}>
                      {available.map((tag) => (
                        <CommandItem key={tag} value={tag} onSelect={() => handleAdd(tag)}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='w-full truncate'>{tag}</span>
                            </TooltipTrigger>
                            <TooltipContent>{tag}</TooltipContent>
                          </Tooltip>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {!showCreate && available.length === 0 && <CommandEmpty>No tags found.</CommandEmpty>}
                </TooltipProvider>
              </CommandList>
            </Command>
            {query.length > 0 && (
              <span
                className={cn(
                  'pointer-events-none absolute right-3 top-2.5 text-xs tabular-nums',
                  trimmed.length >= MAX_TAG_LENGTH ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {trimmed.length}/{MAX_TAG_LENGTH}
              </span>
            )}
          </div>
          {selected.length > 0 && (
            <ScrollArea type='always' className='border-t p-2 gap-2 max-h-22 w-full'>
              {selected.map((tag) => (
                <Badge
                  key={tag}
                  shape='tag'
                  size='tag'
                  variant='secondary'
                  className={cn(tagChipClass, 'pr-1')}
                  wrapperClassName='min-w-0 inline-block pr-1'
                >
                  <span className='opacity-50'>#</span>
                  <MarqueeText speed={25}>{tag}</MarqueeText>
                  <button
                    type='button'
                    aria-label={`Remove ${tag}`}
                    className='shrink-0 rounded-sm hover:text-foreground focus-visible:outline-none'
                    onClick={() => handleRemove(tag)}
                  >
                    <X size={12} />
                  </button>
                </Badge>
              ))}
            </ScrollArea>
          )}
        </PopoverContent>
      </Popover>
    )
  },
)

TagPicker.displayName = 'TagPicker'
